<script setup>

import { inject, ref, computed, onMounted, nextTick } from 'vue';
import { 
    apiGetColonnesImport,
	apiModifierFormuleImportAjoutVariableObjet,
    apiModifierFormuleImportAjoutVariableDimension,
    apiModifierFormuleImportAlimentationChamps,
    apiModifierTypeDonneeColonneImport, 
    apiSupprimerColonnesImport } from '@/services/ServiceModele.js';
import Panel from 'primevue/panel';
import MultiSelect from 'primevue/multiselect';
import SelecteurTypeDonnee from '@/components/generiques/SelecteurTypeDonnee.vue';
import ModaleAjoutVariableObjetImport from '@/components/modales/ModaleAjoutVariableObjetImport.vue';
import ModaleAjoutVariableDimensionImport from '@/components/modales/ModaleAjoutVariableDimensionImport.vue';
import ModaleAlimentationChampsImport from '@/components/modales/ModaleAlimentationChampsImport.vue';
import ModaleModifFichierExempleImport from '@/components/modales/ModaleModifFichierExempleImport.vue';

const { importDonnees } = defineProps({
    importDonnees: Object
});

const emit = defineEmits(['fermeture-fenetre']);

const getTypeDonnee = inject('getTypeDonnee');
const afficherBarreFlottante = inject('afficherBarreFlottante');
const appelerApiModele = inject('appelerApiModele');
const appelerApiModifModele = inject('appelerApiModifModele');
const evenement = inject('evenement');

const zoneRepliee = ref(false);
const colonnes = ref([]);
const extraitFichier = ref([]);
const colonnesSelectionnees = ref([]);
const derniereColonneSelectionnee = ref(null);
const affichageModaleModifFichierExemple = ref(false);
const affichageModaleAjoutVariableObjet = ref(false);
const affichageModaleAjoutVariableDimension = ref(false);
const affichageModaleAlimentationChamps = ref(false);
const boutonMenu = ref(null);

onMounted(() => {
    chargerColonnesImport();
});

const nomsColonnesSelectionnees = computed(() =>
    new Set(colonnesSelectionnees.value.map(col => col.nom))
);

const itemsMenu = computed(() => {
    const items = [];
    items.push({
        libelle: 'Définir un objet à partir des colonnes sélectionnées',
        icones: ['fa fa-id-badge'],
        groupe: 1,
        commande: () =>  ouvrirModaleAjoutVariableObjet()
    });
    items.push({
        libelle: 'Identifier une valeur de dimension à partir de la colonne sélectionnée',
        icones: ['fa fa-ruler-horizontal'],
        desactive: colonnesSelectionnees.value.length !== 1,
        groupe: 1,
        commande: () =>  ouvrirModaleAjoutVariableDimension()
    });
    items.push({
        libelle: 'Alimenter les valeurs d\'un objet à partir des colonnes sélectionnées',
        icones: ['fa fa-table-cells'],
        desactive: colonnesSelectionnees.value.length === 0,
        groupe: 1,
        commande: () =>  ouvrirModaleAlimentationChamps()
    });
    items.push({
        libelle: 'Supprimer les colonnes sélectionnées',
        icones: ['fa fa-trash'],
        style: 'color: var(--couleur-supprimer); ',
        desactive: colonnesSelectionnees.value.length === 0,
        groupe: 2,
        commande: () =>  supprimerColonnes(colonnesSelectionnees.value)
    });
    return items;
});

function chargerColonnesImport() {
    appelerApiModele(apiGetColonnesImport, [importDonnees.id], function(reponseOK, formatCol) {
        if (reponseOK) {
            colonnes.value = formatCol.colonnes;
            colonnes.value.forEach(colonne => {
                colonne.typeDonnee = getTypeDonnee(colonne.typeDonnee);
            });
            extraitFichier.value = formatCol.extraitFichier;
        } else {
            colonnes.value = [];
            extraitFichier.value = [];
            afficherErreur('Erreur lors de la récupération des colonnes de l\'import');
        }
    });
}

function rafraichirColonnesImport() {
    const nomsColSelectionneesAvant = colonnesSelectionnees.value.map(colonne => colonne.nom);
    chargerColonnesImport();
    nextTick(() => {
        colonnesSelectionnees.value = colonnes.value.filter(col => nomsColSelectionneesAvant.includes(col.nom));
    });
}

function selectionnerColonne(colonne, event = null) {
    const nom = colonne.nom;

    if (event && event.shiftKey && derniereColonneSelectionnee.value) {
        const indexDepart = colonnes.value.findIndex(col => col.nom === derniereColonneSelectionnee.value.nom);
        const indexArrivee = colonnes.value.findIndex(col => col.nom === nom);

        if (indexDepart !== -1 && indexArrivee !== -1) {
            const [start, end] = [indexDepart, indexArrivee].sort((a, b) => a - b);
            const intervalle = colonnes.value.slice(start, end + 1);

            const nomsSelectionnes = new Set(colonnesSelectionnees.value.map(c => c.nom));
            const nouvellesColonnes = intervalle.filter(col => !nomsSelectionnes.has(col.nom));
            colonnesSelectionnees.value.push(...nouvellesColonnes);
        }
    } else {
        const index = colonnesSelectionnees.value.findIndex(c => c.nom === nom);
        if (index !== -1) {
            colonnesSelectionnees.value.splice(index, 1);
        } else {
            colonnesSelectionnees.value.push(colonne);
        }
        derniereColonneSelectionnee.value = colonne;
    }
}

function retirerColonneSelectionnee(indiceSelection) {
    colonnesSelectionnees.value.splice(indiceSelection, 1);
}

function ouvrirModaleAjoutVariableObjet() {
    affichageModaleAjoutVariableObjet.value = true;
}

function ouvrirModaleAjoutVariableDimension() {
    affichageModaleAjoutVariableDimension.value = true;
}

function ouvrirModaleAlimentationChamps() {
    affichageModaleAlimentationChamps.value = true;
}

function basculerAffichageMenu() {
    afficherBarreFlottante({
        items: itemsMenu, 
        selectionItems: [],
        composantParent: boutonMenu.value
    });
}

function ajouterVariableObjet(infosAjoutVariableObjet) {
    appelerApiModifModele(apiModifierFormuleImportAjoutVariableObjet, importDonnees, infosAjoutVariableObjet, (reponseOK) => {
        if (reponseOK) {
            affichageModaleAjoutVariableObjet.value = null;
            colonnesSelectionnees.value.length = 0;
        }
    });
}

function ajouterVariableDimension(infosAjoutVariableDimension) {
    appelerApiModifModele(apiModifierFormuleImportAjoutVariableDimension, importDonnees, infosAjoutVariableDimension, (reponseOK) => {
        if (reponseOK) {
            affichageModaleAjoutVariableDimension.value = null;
            colonnesSelectionnees.value.length = 0;
        }
    });
}

function alimenterChamps(infosAlimentationChamps) {
    appelerApiModifModele(apiModifierFormuleImportAlimentationChamps, importDonnees, infosAlimentationChamps, (reponseOK) => {
        if (reponseOK) {
            affichageModaleAlimentationChamps.value = null;
            colonnesSelectionnees.value.length = 0;
        }
    });
}

function modifierTypeDonneeColonne(colonne, typeDonnee) {
    const colonneModifiee = {...colonne};
    colonneModifiee.typeDonnee = typeDonnee;
    appelerApiModifModele(apiModifierTypeDonneeColonneImport, importDonnees, colonneModifiee, (reponseOK) => {
        if (reponseOK) {
            rafraichirColonnesImport();
        }
    });
}

function supprimerColonnes(colonnes) {
    if (confirm(`Voulez-vous vraiment supprimer les colonnes sélectionnées ?`)) {
        appelerApiModifModele(apiSupprimerColonnesImport, importDonnees, colonnes, (reponseOK) => {
            if (reponseOK) {
                colonnesSelectionnees.value.length = 0;
                chargerColonnesImport();
            }
        });
    }
}

function afficherModaleModifFicherExemple() {
    affichageModaleModifFichierExemple.value = true;
}

function afficherModaleImportFichier() {
    emit('fermeture-fenetre');
    evenement.value.affichageModaleImportFichier = importDonnees;
}

</script>

<template>
    <div class="zone-bouton-import-fichier">
        <button 
            class="bouton fa fa-upload"
            v-tooltip="'Importer un fichier'"
            :disabled="importDonnees.formuleEnErreur"
            @click="afficherModaleImportFichier" />
    </div>
    <Panel 
        class="panel-aide-import"
        header="Colonnes de l'import"
        toggleable
        v-model:collapsed="zoneRepliee"
        :pt="{
            header: { style: 'justify-content: flex-start; gap: 5px' }
        }">
            <template #togglericon="{ collapsed }">
                <i class="fas" :class="collapsed ? 'bouton fa-solid fa-caret-down' : 'bouton fa-solid fa-caret-up'"></i>
            </template>
            <div class="zone-aide-formule-import">
            <div class="zone-boutons-tableau-valeurs">
                <button 
                    class="bouton fa fa-repeat"
                    v-tooltip="'Changer le fichier d\'exemple utilisé'"
                    @click="afficherModaleModifFicherExemple" />
            </div>
            <div 
                class="tableau-colonnes"
                :style="{gridTemplateColumns: Array(colonnes.length).fill('minmax(auto, 200px)').join(' ')}">
                <div v-for="colonne in colonnes" :key="colonne.nom">
                    <div 
                        class="cellule entete-colonne"
                        @click="selectionnerColonne(colonne, $event)">
                        <div class="zone-nom-colonne">{{ colonne.nom }}</div>
                        <SelecteurTypeDonnee 
                            style="width: 100%; "
                            v-model:typeDonnee="colonne.typeDonnee"
                            @click.stop=""
                            @modification="(typeDonnee) => modifierTypeDonneeColonne(colonne, typeDonnee)"/>
                    </div>
                </div>
                <template v-for="(ligne, indiceLigne) in extraitFichier" :key="`ligne_${indiceLigne}`">
                    <div v-for="(valeur, indiceColonne) in ligne"
                        :key="`val_${indiceLigne}_${indiceColonne}`"
                        @click="selectionnerColonne(colonnes[indiceColonne], $event)">
                        <div 
                            class="cellule"
                            :class="{'colonne-selectionnee': nomsColonnesSelectionnees.has(colonnes[indiceColonne].nom)}">
                                {{ valeur }}
                            </div>
                    </div>
                </template>
            </div>
            <div class="zone-action-selection">
                <div class="intitule"> Colonne(s) sélectionnée(s) :</div>
                <MultiSelect
                    class="liste-col-selectionnees"
                    v-model="colonnesSelectionnees"
                    :options="colonnes"
                    optionLabel="nom"
                    placeholder="Aucune colonne"
                    :filter="true"
                    :showToggleAll="true"
                    display="chip">
                    <template #value="slotProps">
                        <div class="p-multiselect-token" v-for="(colonne, indice) in slotProps.value" :key="indice">
                        <span class="p-multiselect-token-label">{{ colonne.nom }}</span>
                        <span
                            class="p-multiselect-token-icon pi pi-times"
                            @mousedown.stop
                            @click="retirerColonneSelectionnee(indice)"
                        ></span>
                        </div>
                    </template>
                </MultiSelect>
                <button
                :style="{visibility: (colonnesSelectionnees.length ? 'visible' : 'hidden')}"
                class="bouton btn-vider-selection pi pi-times"
                v-tooltip="'Vider la sélection'"
                @click="colonnesSelectionnees.length = 0" />
                <button 
                    class="bouton" 
                    ref="boutonMenu"
                    v-tooltip="'Compléter la formule d\'import'"
                    @click.stop="basculerAffichageMenu">
                    <i class="fa-solid fa-wand-magic-sparkles"></i>
                    <i class="fa-solid fa-caret-down"></i>
                </button>
            </div>
            <ModaleAjoutVariableObjetImport 
                v-if="affichageModaleAjoutVariableObjet"
                :colonnes-selectionnees="colonnesSelectionnees"
                :variables="importDonnees.formule.variables"
                @ajout-variable-objet="ajouterVariableObjet"
                @fermeture="affichageModaleAjoutVariableObjet = false" />
            <ModaleAjoutVariableDimensionImport 
                v-if="affichageModaleAjoutVariableDimension"
                :colonne-selectionnee="colonnesSelectionnees[0]"
                @ajout-variable-dimension="ajouterVariableDimension"
                @fermeture="affichageModaleAjoutVariableDimension = false" />
            <ModaleAlimentationChampsImport 
                v-if="affichageModaleAlimentationChamps"
                :variables="importDonnees.formule.variables"
                :colonnes-selectionnees="colonnesSelectionnees"
                @alimentation-champs="alimenterChamps"
                @fermeture="affichageModaleAlimentationChamps = false" />
            <ModaleModifFichierExempleImport 
                v-model:visible="affichageModaleModifFichierExemple"
                :import-donnees="importDonnees"
                @modif-fichier="rafraichirColonnesImport()" />
        </div>
    </Panel>
</template>

<style scoped>

.zone-bouton-import-fichier {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-bottom: 5px;
    margin-right: 2px;
}

.panel-aide-import {
    margin-bottom: 5px;
}

.zone-aide-formule-import {
    display: flex;
    flex-direction: column;
    align-items: stretch;    
    gap: 10px;
    max-width: 100%;
    top: 0;
    z-index: 1;
    background-color: white;
}

.zone-boutons-tableau-valeurs {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
}

.tableau-colonnes {
    display: grid;
    justify-items: stretch;
    overflow-x: auto;
    gap: 0; 
    width: 100%;
    padding: 5px;
    border: 2px solid var(--couleur-bordure);
    border-radius: 2px;
}

.zone-nom-colonne {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;    
}

.cellule {
    border: 1px solid var(--couleur-bordure) !important;
    overflow: hidden;
    background-color: white;
    opacity: 100%;
    cursor: pointer;
    white-space: nowrap;
    width: 100%;
}

.cellule * {
    cursor: pointer;
}

.zone-action-selection {
    display: flex;
    align-items: center;
}

.liste-col-selectionnees {
    flex-grow: 1;
    margin-left: 5px;
    width: 10vw;
    margin-right: 2px;
}

.bouton {
    display: flex;
    gap: 5px;
    height: 2rem;
    align-items: center;
    width: fit-content;
}

.btn-vider-selection {
    margin-right: 20px;
}

.colonne-selectionnee {
    background-color: var(--couleur-fond-zone-select);
}

</style>